import React, { useEffect, useState } from "react";
import { IonContent, IonPage, useIonToast } from "@ionic/react";
import loginAPI from "../services/login";
import { isWebAuthnSupported, webauthnLogin, webauthnDispositivoLogin, isMobileDevice } from "src/services/webauthn";
import { Warning } from "@material-ui/icons";
import { InputAdornment, TextField } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import "./Login.css";
import {
  EstadoCedula,
  EstadoContraseña,
  EstadoLogin,
  LoginProps,
} from "src/utils";

const Login: React.FC<LoginProps> = (props: LoginProps) => {
  const [estado, setEstado] = useState<EstadoLogin>("ESPERANDO_CEDULA");
  const [cedula, setCedula] = useState<EstadoCedula>({
    valor: "",
    valido: false,
    error: false,
    ayuda: "",
    ocultarAyuda: false,
  });
  const [contraseña, setContraseña] = useState<EstadoContraseña>({
    valor: "",
    estado: "invisible",
    valido: false,
    error: false,
    ayuda: "",
  });
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [present] = useIonToast();

  useEffect(() => {
    return () => {};
  }, []);

  const handleClick = async () => {
    if (estado === "ESPERANDO_API") return;

    if (!cedula.valido) {
      updateCedula(undefined, undefined, !cedula.valido);
      return;
    } else if (contraseña.estado === "visible" && !contraseña.valido) {
      updateContraseña(undefined, undefined, undefined, !contraseña.valido);
      return;
    }

    if (estado === "ESPERANDO_CEDULA") {
      updateEstado("ESPERANDO_API");
      const ced = parseInt(cedula.valor);

      // 1) Intento de login por CLAVE DE DISPOSITIVO (passwordless, sin código).
      //    SOLO en móvil: la clave vive en el celular. Desde la PC no se intenta
      //    (evita el "escaneá con tu celular") y se va directo al código.
      if (isWebAuthnSupported() && isMobileDevice()) {
        try {
          const resp = await webauthnDispositivoLogin(ced);
          present({ message: resp.data.info, duration: 5000 });
          updateCedula("", true, false, "", false);
          updateContraseña("", "invisible", false, false, "");
          updateEstado("ESPERANDO_CEDULA");
          props.agregarUsuario(resp.data);
          return;
        } catch (error: any) {
          // 404 = sin clave de dispositivo; cualquier error (cancelación/no soportado)
          // cae al flujo de código SMS de abajo.
        }
      }

      // 2) Flujo actual: solicitar el código por SMS.
      loginAPI
        .solicitarContraseña(ced)
        .then((response) => {
          updateCedula(undefined, undefined, undefined, undefined, true);
          updateContraseña("", "visible", false, false, "");
          updateEstado("ESPERANDO_CONTRASEÑA");
          present({ message: response.data.info, duration: 5000 });
        })
        .catch((error) => {
          updateEstado("ESPERANDO_CEDULA");
          present({
            message: error.response ? error.response.data.error : error,
            duration: 5000,
          });
        });
    } else if (estado === "ESPERANDO_CONTRASEÑA") {
      updateEstado("ESPERANDO_API");

      const cedulaIngresada = parseInt(cedula.valor);
      const contraseñaIngresada = contraseña.valor.toUpperCase();

      loginAPI
        .login(cedulaIngresada, contraseñaIngresada)
        .then((response) => {
          present({ message: response.data.info, duration: 5000 });

          // Aviso de dispositivo nuevo: el backend detecto que este operario inicio
          // sesion desde un dispositivo distinto al registrado como seguro.
          if (response?.data?.dispositivo_cambiado) {
            present({
              message:
                'Aviso: iniciaste sesión desde un dispositivo distinto al habitual. Este equipo quedó registrado como tu nuevo dispositivo.',
              duration: 8000,
              color: 'warning',
            });
          }

          // Mostrar aviso especial para el cargo de DESARROLLADOR FRONT END
          const cargoDetectado = (response?.data?.cargo || '').toUpperCase(); // normalizar cargo
          if (cargoDetectado.includes('DESARROLLADOR FRONT END')) { // validar coincidencia de cargo
            present({ message: 'ACTUALIZACION 22/10/2025', duration: 5000, color: 'warning' }); // mostrar mensaje solicitado
          }
          // El ofrecimiento de registrar el dispositivo (piloto) lo maneja
          // DispositivoSeguroPrompt tras el login, sobre /usuario/mi_informacion.

          updateCedula("", true, false, "", false);
          updateContraseña("", "invisible", false, false, "");
          updateEstado("ESPERANDO_CEDULA");

          props.agregarUsuario(response.data);
        })
        .catch((error) => {
          updateEstado("ESPERANDO_CONTRASEÑA");
          present({
            message: error.response ? error.response.data.error : error,
            duration: 5000,
          });
        });
    }
  };

  const handlePasskeyLogin = async () => {
    if (estado === "ESPERANDO_API" || passkeyLoading) return;

    if (!cedula.valido) {
      updateCedula(undefined, undefined, !cedula.valido);
      return;
    }

    if (!isWebAuthnSupported()) {
      present({ message: "Passkey disponible solo en dispositivos compatibles con HTTPS.", duration: 5000, color: "warning" });
      return;
    }

    updateEstado("ESPERANDO_API");
    setPasskeyLoading(true);
    try {
      const cedulaIngresada = parseInt(cedula.valor);
      const response = await webauthnLogin(cedulaIngresada);

      present({ message: response.data.info, duration: 5000 });
      updateCedula("", true, false, "", false);
      updateContraseña("", "invisible", false, false, "");
      updateEstado("ESPERANDO_CEDULA");

      props.agregarUsuario(response.data);
    } catch (error: any) {
      updateEstado("ESPERANDO_CEDULA");
      present({
        message: error.response ? error.response.data.error : error,
        duration: 5000,
      });
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const input = event.target as HTMLInputElement;

    if (input.name === "cedula") updateInputCedula(input.value);
    else if (input.name === "contraseña") updateInputContraseña(input.value);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const boton = event.key;

    if (boton === "Enter") {
      const target = event.target as HTMLInputElement;
      const input = target.name;

      if (input === "contraseña" || input === "cedula") handleClick();
    }
  };

  const updateInputCedula = (input: string) => {
    const cedula = input;
    const valido = esEntero(cedula);

    let ayuda = "";
    if (cedula.length === 0) ayuda = "Cédula obligatoria";
    else if (!valido) ayuda = "La cédula sólo debe tener números";

    updateCedula(cedula, valido, !valido, ayuda, false);
    updateContraseña("", "invisible", false, false, "");
    updateEstado("ESPERANDO_CEDULA");
  };

  const updateInputContraseña = (input: string) => {
    const contraseña = input;
    const valido = contraseña.length > 0;

    let ayuda = "";
    if (!valido) ayuda = "La contraseña es obligatoria";

    updateContraseña(contraseña, undefined, valido, !valido, ayuda);
    updateEstado("ESPERANDO_CONTRASEÑA");
  };

  const updateCedula = (
    valor: string = cedula.valor,
    valido: boolean = cedula.valido,
    error: boolean = cedula.error,
    ayuda: string = cedula.ayuda,
    ocultarAyuda: boolean = cedula.ocultarAyuda
  ) => {
    setCedula({
      valor: valor,
      valido: valido,
      error: error,
      ayuda: ayuda,
      ocultarAyuda: ocultarAyuda,
    });
  };

  const updateContraseña = (
    valor: string = contraseña.valor,
    estado: string = contraseña.estado,
    valido: boolean = contraseña.valido,
    error: boolean = contraseña.error,
    ayuda: string = contraseña.ayuda
  ) => {
    setContraseña({
      valor: valor,
      estado: estado,
      valido: valido,
      error: error,
      ayuda: ayuda,
    });
  };

  const updateEstado = (estado: EstadoLogin) => {
    setEstado(estado);
  };

  const claseCedula = `w-help-text margin-transition ${
    cedula.error ? "pb-0" : "pb-2"
  }`;

  const claseContraseña = `w-help-text margin-transition ${
    contraseña.error ? "pb-0" : "pb-2"
  }`;

  const passkeyVisible = isWebAuthnSupported();

  return (
    <IonPage>
      <IonContent>
        <div className="d-flex flex-column equal-size-children h-100">
          <div className="d-flex flex-centered-content h-100 w-100">
            <div className="logo">
              <img
                className="img-fluid"
                src="assets/images/el-mejor.png"
                alt=""
              />
            </div>
          </div>
          <div className="d-flex flex-column align-items-center justify-content-between">
            <form className="d-flex flex-column align-items-center">
              <TextField
                name="cedula"
                label="Cédula"
                variant="outlined"
                className={claseCedula}
                helperText={cedula.ayuda}
                value={cedula.valor}
                onChange={(event) => handleChange(event)}
                onKeyPress={(event) => handleKeyPress(event)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {cedula.error && <Warning color="secondary" />}
                    </InputAdornment>
                  ),
                }}
              />

              <div className={"visibility-switch " + contraseña.estado}>
                <TextField
                  name="contraseña"
                  label="Contraseña"
                  variant="outlined"
                  className={claseContraseña}
                  helperText={contraseña.ayuda}
                  value={contraseña.valor}
                  onChange={(event) => handleChange(event)}
                  onKeyPress={(event) => handleKeyPress(event)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {contraseña.error && <Warning color="secondary" />}
                      </InputAdornment>
                    ),
                  }}
                />
              </div>

              <Button
                className="button"
                variant="contained"
                color="primary"
                onClick={() => handleClick()}
              >
                INICIAR SESIÓN
              </Button>
            </form>
            {passkeyVisible && (
              <div className="login-passkey-section">
                <Button
                  className="button login-passkey-button"
                  variant="outlined"
                  color="primary"
                  onClick={() => handlePasskeyLogin()}
                  disabled={estado === "ESPERANDO_API" || passkeyLoading}
                >
                  {passkeyLoading ? "INICIANDO..." : "INICIAR CON BIOMETRICO"}
                </Button>
              </div>
            )}
            <span className="caption text-center p-5">
              Si cambiaste de número, comunicate con{" "}
              <a className="nowrap" href="tel:+595971540413">
                Contact Center
              </a>
              , por favor.
            </span>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Login;

function esEntero(cadena: string) {
  return /^([0-9]|[1-9][0-9]+)$/.test(cadena);
}
